/**
 * Status, filter, and display config for programs.
 */
import {
  FileText,
  CheckCircle2,
  Clock,
  Ban,
  MapPin,
  Monitor,
  Layers,
  type LucideIcon,
} from 'lucide-react';
import type { BadgeVariant } from '@/components/common/LabelBadge';
import { COURSE_PATH_TYPE_CONFIG, SUPPORT_TYPE_LABEL } from '@/constants/shared-catalog';

export { COURSE_PATH_TYPE_CONFIG, SUPPORT_TYPE_LABEL };

/** Badge + icon config for a program status. */
export interface ProgramStatusConfig {
  label: string;
  icon: LucideIcon;
  badgeVariant: BadgeVariant;
  badgeColor?: string;
}

/** Badge config for each program status. */
export const PROGRAM_STATUS_CONFIG: Record<string, ProgramStatusConfig> = {
  draft: {
    label: 'Brouillon',
    icon: FileText,
    badgeVariant: 'outlined',
    badgeColor: 'var(--color-1-nuance-1)',
  },
  active: {
    label: 'Actif',
    icon: CheckCircle2,
    badgeVariant: 'active',
  },
  suspended: {
    label: 'Suspendu',
    icon: Clock,
    badgeVariant: 'outlined',
    badgeColor: 'var(--color-3-nuance-1)',
  },
  closed: {
    label: 'Clôturé',
    icon: Ban,
    badgeVariant: 'outlined',
    badgeColor: 'rgb(var(--color-error))',
  },
};

/** Program format: in-person, remote, or hybrid. */
export type ProgramType = 'in_person' | 'remote' | 'hybrid';

/** Icon + label config for a program format. */
export interface ProgramTypeConfig {
  label: string;
  icon: LucideIcon;
}

/** Badge config for each program format. */
export const PROGRAM_TYPE_CONFIG: Record<ProgramType, ProgramTypeConfig> = {
  in_person: {
    label: SUPPORT_TYPE_LABEL.in_person,
    icon: MapPin,
  },
  remote: {
    label: SUPPORT_TYPE_LABEL.remote,
    icon: Monitor,
  },
  hybrid: {
    label: SUPPORT_TYPE_LABEL.hybrid,
    icon: Layers,
  },
};

/** Filter group IDs for the programs {@link DataTable} configure panel. */
export type FilterGroupId = 'filter-status' | 'filter-type';

/** Program list filter state. */
export interface ProgramFilters {
  statuses: string[];
  types: ProgramType[];
}

/** Default program list filters. */
export const DEFAULT_PROGRAM_FILTERS: ProgramFilters = {
  statuses: [],
  types: [],
};

/** Configure-panel order for program filter groups. */
export const ALL_FILTER_GROUP_IDS: FilterGroupId[] = ['filter-status', 'filter-type'];

/** Human-readable label per filter group (configure panel). */
export const FILTER_GROUP_LABELS: Record<FilterGroupId, string> = {
  'filter-status': 'Statut',
  'filter-type': 'Type',
};

/** Returns the badge color name for a program status. */
export function getProgramStatusColor(status: string): 'success' | 'error' {
  return status === 'active' ? 'success' : 'error';
}

/** {@link Select} options for program status. */
export const PROGRAM_STATUS_FORM_OPTIONS = Object.entries(PROGRAM_STATUS_CONFIG).map(
  ([value, item]) => ({ value, label: item.label }),
);

/** {@link Select} options for course path type. */
export const PROGRAM_COURSE_PATH_FORM_OPTIONS = Object.entries(COURSE_PATH_TYPE_CONFIG).map(
  ([value, item]) => ({ value, label: item.label }),
);

/** React Query keys invalidated after a program create / save. */
export const PROGRAM_FORM_QUERY_KEYS = {
  list: ['programs'] as const,
  one: (id: string) => ['program', id] as const,
};
